import { useState, useEffect, useRef, useCallback } from 'react'
import {
  ConsentRecord,
  PersonProfile,
  SessionRecord,
  EventRecord,
  ChatRecord,
  EventType,
  EventCategory,
  ChatIntent,
  Sentiment,
  RiskFlag,
} from '../types/analytics.types'

// Generate unique IDs
const generateId = (prefix: string): string => {
  return `${prefix}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
}

// Generate browser fingerprint (privacy-friendly alternative to IP)
const generateBrowserFingerprint = (): string => {
  const canvas = document.createElement('canvas')
  const ctx = canvas.getContext('2d')
  if (ctx) {
    ctx.textBaseline = 'top'
    ctx.font = '14px Arial'
    ctx.fillText('fingerprint', 2, 2)
  }

  const fingerprint = {
    userAgent: navigator.userAgent,
    language: navigator.language,
    languages: navigator.languages?.join(',') || '',
    platform: navigator.platform,
    hardwareConcurrency: navigator.hardwareConcurrency || 0,
    deviceMemory: (navigator as any).deviceMemory || 0,
    screenResolution: `${window.screen.width}x${window.screen.height}`,
    colorDepth: window.screen.colorDepth,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    timezoneOffset: new Date().getTimezoneOffset(),
    canvas: canvas.toDataURL(),
  }

  const fingerprintString = JSON.stringify(fingerprint)

  // Simple hash function
  let hash = 0
  for (let i = 0; i < fingerprintString.length; i++) {
    const char = fingerprintString.charCodeAt(i)
    hash = (hash << 5) - hash + char
    hash = hash & hash
  }

  return `fp_${Math.abs(hash).toString(36)}`
}

// Parse user agent
const parseUserAgent = (ua: string) => {
  const browserMatch = ua.match(/(Edge|Chrome|Firefox|Safari|Opera)\/(\d+)/)
  const browser = browserMatch ? browserMatch[1] : 'Unknown'
  const browserVersion = browserMatch ? browserMatch[2] : ''

  const osMatch = ua.match(
    /(Windows NT \d+\.\d+|Mac OS X \d+[._]\d+|Linux|Android \d+|iOS \d+)/,
  )
  const os = osMatch ? osMatch[1].replace(/_/g, '.') : 'Unknown'
  const osVersion = ''

  const device = /Mobile|Android|iPhone|iPad/.test(ua)
    ? /iPad/.test(ua)
      ? 'Tablet'
      : 'Mobile'
    : 'Desktop'

  return { browser, browserVersion, os, osVersion, device }
}

// Get approximate location from timezone (COMPLETELY SILENT - NO API CALLS)
const getLocationFromTimezone = () => {
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone

  // Map common timezones to regions (approximate, privacy-friendly)
  const timezoneMap: {
    [key: string]: { region: string; country: string; country_code: string }
  } = {
    'America/New_York': {
      region: 'Eastern US',
      country: 'United States',
      country_code: 'US',
    },
    'America/Chicago': {
      region: 'Central US',
      country: 'United States',
      country_code: 'US',
    },
    'America/Denver': {
      region: 'Mountain US',
      country: 'United States',
      country_code: 'US',
    },
    'America/Los_Angeles': {
      region: 'Pacific US',
      country: 'United States',
      country_code: 'US',
    },
    'America/Phoenix': {
      region: 'Mountain US',
      country: 'United States',
      country_code: 'US',
    },
    'America/Toronto': {
      region: 'Eastern Canada',
      country: 'Canada',
      country_code: 'CA',
    },
    'America/Vancouver': {
      region: 'Western Canada',
      country: 'Canada',
      country_code: 'CA',
    },
    'Europe/London': {
      region: 'UK',
      country: 'United Kingdom',
      country_code: 'GB',
    },
    'Europe/Paris': {
      region: 'Western Europe',
      country: 'France',
      country_code: 'FR',
    },
    'Europe/Berlin': {
      region: 'Central Europe',
      country: 'Germany',
      country_code: 'DE',
    },
    'Europe/Madrid': {
      region: 'Southern Europe',
      country: 'Spain',
      country_code: 'ES',
    },
    'Europe/Rome': {
      region: 'Southern Europe',
      country: 'Italy',
      country_code: 'IT',
    },
    'Asia/Tokyo': { region: 'East Asia', country: 'Japan', country_code: 'JP' },
    'Asia/Shanghai': {
      region: 'East Asia',
      country: 'China',
      country_code: 'CN',
    },
    'Asia/Singapore': {
      region: 'Southeast Asia',
      country: 'Singapore',
      country_code: 'SG',
    },
    'Asia/Dubai': { region: 'Middle East', country: 'UAE', country_code: 'AE' },
    'Asia/Kolkata': {
      region: 'South Asia',
      country: 'India',
      country_code: 'IN',
    },
    'Asia/Hong_Kong': {
      region: 'East Asia',
      country: 'Hong Kong',
      country_code: 'HK',
    },
    'Asia/Seoul': {
      region: 'East Asia',
      country: 'South Korea',
      country_code: 'KR',
    },
    'Asia/Bangkok': {
      region: 'Southeast Asia',
      country: 'Thailand',
      country_code: 'TH',
    },
    'Asia/Ho_Chi_Minh': {
      region: 'Southeast Asia',
      country: 'Vietnam',
      country_code: 'VN',
    },
    'Australia/Sydney': {
      region: 'Australia',
      country: 'Australia',
      country_code: 'AU',
    },
    'Australia/Melbourne': {
      region: 'Australia',
      country: 'Australia',
      country_code: 'AU',
    },
    'Pacific/Auckland': {
      region: 'New Zealand',
      country: 'New Zealand',
      country_code: 'NZ',
    },
  }

  const location = timezoneMap[timezone] || {
    region: 'Unknown',
    country: 'Unknown',
    country_code: 'XX',
  }

  return {
    timezone,
    region: location.region,
    country: location.country,
    country_code: location.country_code,
  }
}

// Detect language from text
const detectLanguage = (text: string): 'en' | 'vi' | 'unknown' => {
  const lower = text.toLowerCase()

  const hasDiacritics =
    /[àáạảãâầấậẩẫăằắặẳẵèéẹẻẽêềếệểễìíịỉĩòóọỏõôồốộổỗơờớợởỡùúụủũưừứựửữỳýỵỷỹđ]/.test(
      lower,
    )
  const vietnameseWords = [
    'là',
    'của',
    'và',
    'những',
    'các',
    'trong',
    'có',
    'không',
    'gì',
    'tại',
    'cho',
    'về',
    'người',
    'này',
    'anh',
    'em',
    'bạn',
  ]
  const hasVietnameseWords = vietnameseWords.some((word) => {
    const pattern = new RegExp(`(^|\\s)${word}($|\\s)`, 'i')
    return pattern.test(lower)
  })

  if (hasDiacritics || hasVietnameseWords) return 'vi'

  const englishWords = [
    'the',
    'is',
    'are',
    'was',
    'were',
    'have',
    'has',
    'had',
    'do',
    'does',
    'did',
    'will',
    'would',
    'can',
    'could',
    'should',
  ]
  const hasEnglishWords = englishWords.some((word) => {
    const pattern = new RegExp(`(^|\\s)${word}($|\\s)`, 'i')
    return pattern.test(lower)
  })

  if (hasEnglishWords) return 'en'
  return 'unknown'
}

// Classify chat intent
const classifyChatIntent = (message: string): ChatIntent => {
  const lower = message.toLowerCase()
  if (/^(hi|hello|hey|greetings|xin chào|chào)/i.test(message))
    return 'greeting'
  if (/\?/.test(message)) return 'question'
  if (/(please|can you|could you|would you|làm ơn|giúp)/i.test(lower))
    return 'request'
  if (/(thank|thanks|appreciate|cảm ơn)/i.test(lower)) return 'praise'
  if (/(bad|terrible|awful|disappointed|tệ|kém)/i.test(lower))
    return 'complaint'
  if (
    /(what|why|how|when|where|who|gì|sao|như thế nào|khi nào|ở đâu|ai)/i.test(
      lower,
    )
  )
    return 'clarification'
  return 'other'
}

// Analyze sentiment
const analyzeSentiment = (message: string): Sentiment => {
  const lower = message.toLowerCase()
  const positiveWords = [
    'great',
    'excellent',
    'amazing',
    'love',
    'perfect',
    'awesome',
    'fantastic',
    'wonderful',
    'tuyệt',
    'tốt',
    'hay',
    'xuất sắc',
  ]
  const negativeWords = [
    'bad',
    'terrible',
    'awful',
    'hate',
    'worst',
    'horrible',
    'disappointing',
    'poor',
    'tệ',
    'kém',
    'dở',
  ]

  const positiveCount = positiveWords.filter((word) =>
    lower.includes(word),
  ).length
  const negativeCount = negativeWords.filter((word) =>
    lower.includes(word),
  ).length

  if (positiveCount > negativeCount) return 'positive'
  if (negativeCount > positiveCount) return 'negative'
  if (positiveCount > 0 && negativeCount > 0) return 'mixed'
  return 'neutral'
}

// Detect risk flags
const detectRiskFlags = (message: string): RiskFlag[] => {
  const flags: RiskFlag[] = []
  const lower = message.toLowerCase()

  if (/(.)\1{10,}/.test(message) || message.length > 1000) {
    flags.push('spam')
  }

  const profanityWords = [
    'fuck',
    'shit',
    'damn',
    'bitch',
    'asshole',
    'đéo',
    'địt',
    'lồn',
    'cặc',
  ]
  if (profanityWords.some((word) => lower.includes(word))) {
    flags.push('profanity')
  }

  if (
    /(phone|email|address|ssn|credit card|password|số điện thoại|địa chỉ|mật khẩu)/i.test(
      lower,
    )
  ) {
    flags.push('personal_info_request')
  }

  return flags
}

// Safe localStorage operations
const safeLocalStorageSet = (key: string, value: any) => {
  try {
    localStorage.setItem(key, JSON.stringify(value))
    return true
  } catch (error) {
    return false
  }
}

const safeLocalStorageGet = (key: string): any => {
  try {
    const data = localStorage.getItem(key)
    return data ? JSON.parse(data) : null
  } catch (error) {
    return null
  }
}

export const useEnhancedAnalytics = () => {
  const [hasConsent, setHasConsent] = useState(false)
  const [consentRecord, setConsentRecord] = useState<ConsentRecord | null>(null)
  const [personProfile, setPersonProfile] = useState<PersonProfile | null>(null)
  const [sessionRecord, setSessionRecord] = useState<SessionRecord | null>(null)
  const [events, setEvents] = useState<EventRecord[]>([])
  const [chats, setChats] = useState<ChatRecord[]>([])
  const [conversationHistory, setConversationHistory] = useState<
    Array<{
      role: string
      content: string
      timestamp: string
      language: string
    }>
  >([])

  const sessionStartTime = useRef<number>(Date.now())
  const lastEventTime = useRef<number>(Date.now())
  const lastActivityTime = useRef<number>(Date.now())
  const isIdle = useRef(false)
  const idleTimeout = useRef<NodeJS.Timeout>()
  const eventQueue = useRef<EventRecord[]>([])
  const activityInterval = useRef<NodeJS.Timeout>()
  const saveInterval = useRef<NodeJS.Timeout>()

  // Check for existing consent
  useEffect(() => {
    const stored = safeLocalStorageGet('consent_record')
    if (stored) {
      if (stored.agreed && !stored.revoked) {
        setHasConsent(true)
        setConsentRecord(stored)
        initializeSession(stored)
      }
    }
  }, [])

  // Initialize session (COMPLETELY SILENT - NO IP TRACKING)
  const initializeSession = async (consent: ConsentRecord) => {
    const sessionId = generateId('session')

    // Generate browser fingerprint (silent, local only)
    const fingerprint = generateBrowserFingerprint()

    // Get location from timezone (silent, local only)
    const location = getLocationFromTimezone()

    const ua = navigator.userAgent
    const { browser, browserVersion, os, osVersion, device } =
      parseUserAgent(ua)

    // Check for existing person profile
    let person: PersonProfile | null = safeLocalStorageGet('person_profile')

    if (person) {
      person.last_seen = new Date().toISOString()
      person.total_sessions += 1
      safeLocalStorageSet('person_profile', person)
      setPersonProfile(person)
    }

    const session: SessionRecord = {
      session_id: sessionId,
      person_id: person?.person_id,
      consent_id: consent.consent_id,

      // NO IP TRACKING - ALL REMOVED
      ip_address: undefined,
      ip_hash: fingerprint,
      city: undefined,
      region: location.region,
      country: location.country,
      country_code: location.country_code,
      timezone: location.timezone,
      isp: undefined,
      latitude: undefined,
      longitude: undefined,

      user_agent: ua,
      browser,
      browser_version: browserVersion,
      os,
      os_version: osVersion,
      device,
      screen_resolution: `${window.screen.width}x${window.screen.height}`,
      viewport_size: `${window.innerWidth}x${window.innerHeight}`,
      language: navigator.language,

      start_time: new Date().toISOString(),
      duration: 0,
      active_time: 0,
      idle_time: 0,

      referrer: document.referrer || 'Direct',
      utm_source:
        new URLSearchParams(window.location.search).get('utm_source') ||
        undefined,
      utm_medium:
        new URLSearchParams(window.location.search).get('utm_medium') ||
        undefined,
      utm_campaign:
        new URLSearchParams(window.location.search).get('utm_campaign') ||
        undefined,
      utm_term:
        new URLSearchParams(window.location.search).get('utm_term') ||
        undefined,
      utm_content:
        new URLSearchParams(window.location.search).get('utm_content') ||
        undefined,

      page_views: 1,
      sections_viewed: [],
      scroll_depth_max: 0,
      clicks: 0,
      chat_messages: 0,
      files_uploaded: 0,

      bounce: true,
    }

    setSessionRecord(session)
    saveSession(session)

    // Track initial page view
    trackEvent({
      event_type: 'page_view',
      event_category: 'page',
      page_url: window.location.href,
      page_title: document.title,
      metadata: { initial: true },
    })

    // Start activity tracking
    startActivityTracking()

    // Start periodic save
    startPeriodicSave()
  }

  const startPeriodicSave = () => {
    saveInterval.current = setInterval(() => {
      if (eventQueue.current.length > 0) {
        saveEvents(eventQueue.current)
        eventQueue.current = []
      }

      if (sessionRecord) {
        saveSession(sessionRecord)
      }
    }, 10000)
  }

  const startActivityTracking = () => {
    activityInterval.current = setInterval(() => {
      if (sessionRecord) {
        const now = Date.now()
        const totalDuration = Math.floor(
          (now - sessionStartTime.current) / 1000,
        )
        const timeSinceActivity = Math.floor(
          (now - lastActivityTime.current) / 1000,
        )

        const isCurrentlyIdle = timeSinceActivity > 30

        const updatedSession = {
          ...sessionRecord,
          duration: totalDuration,
          active_time: isCurrentlyIdle
            ? sessionRecord.active_time
            : sessionRecord.active_time + 5,
          idle_time: isCurrentlyIdle
            ? sessionRecord.idle_time + 5
            : sessionRecord.idle_time,
          end_time: new Date().toISOString(),
          bounce: sessionRecord.page_views <= 1,
        }

        setSessionRecord(updatedSession)
      }
    }, 5000)
  }

  const handleConsent = async (
    agreed: boolean,
    userData?: { name?: string; role?: string; company?: string },
  ) => {
    const sessionId = generateId('session')
    const consentId = generateId('consent')

    const consent: ConsentRecord = {
      consent_id: consentId,
      session_id: sessionId,
      consent_version: 'v1.0',
      consent_timestamp: new Date().toISOString(),
      consent_text:
        'Full data collection consent as described in privacy notice',
      ip_address: undefined, // NO IP
      user_agent: navigator.userAgent,
      agreed,
    }

    if (agreed) {
      safeLocalStorageSet('consent_record', consent)
      setConsentRecord(consent)
      setHasConsent(true)

      if (userData && (userData.name || userData.role || userData.company)) {
        const personId = generateId('person')
        const person: PersonProfile = {
          person_id: personId,
          name: userData.name,
          role: userData.role,
          company: userData.company,
          first_seen: new Date().toISOString(),
          last_seen: new Date().toISOString(),
          total_sessions: 1,
          total_time_spent: 0,
          consent_records: [consentId],
        }

        safeLocalStorageSet('person_profile', person)
        setPersonProfile(person)
        consent.person_id = personId
      }

      await initializeSession(consent)
    }
  }

  const trackEvent = useCallback(
    (eventData: Partial<EventRecord>) => {
      if (!hasConsent || !sessionRecord) return

      try {
        const event: EventRecord = {
          event_id: generateId('event'),
          session_id: sessionRecord.session_id,
          person_id: personProfile?.person_id,
          event_type: eventData.event_type!,
          event_category: eventData.event_category!,
          timestamp: new Date().toISOString(),
          page_url: eventData.page_url || window.location.href,
          page_title: eventData.page_title || document.title,
          element_type: eventData.element_type,
          element_id: eventData.element_id,
          element_class: eventData.element_class,
          element_text: eventData.element_text,
          viewport_x: eventData.viewport_x,
          viewport_y: eventData.viewport_y,
          scroll_position: eventData.scroll_position,
          scroll_depth: eventData.scroll_depth,
          metadata: eventData.metadata || {},
          time_on_page: Math.floor(
            (Date.now() - sessionStartTime.current) / 1000,
          ),
          time_since_last_event: Math.floor(
            (Date.now() - lastEventTime.current) / 1000,
          ),
        }

        lastEventTime.current = Date.now()
        lastActivityTime.current = Date.now()
        eventQueue.current.push(event)
        setEvents((prev) => [...prev, event])

        if (eventQueue.current.length >= 5) {
          saveEvents(eventQueue.current)
          eventQueue.current = []
        }
      } catch (error) {
        // Silent failure
      }
    },
    [hasConsent, sessionRecord, personProfile],
  )

  const trackChatMessage = useCallback(
    (
      userMessage: string,
      aiResponse: string,
      typingDuration: number,
      editCount: number,
      responseLatency: number,
    ) => {
      if (!hasConsent || !sessionRecord) return

      try {
        const userLanguage = detectLanguage(userMessage)
        const aiLanguage = detectLanguage(aiResponse)
        const intent = classifyChatIntent(userMessage)
        const sentiment = analyzeSentiment(userMessage)
        const riskFlags = detectRiskFlags(userMessage)

        const timestamp = new Date().toISOString()

        const newConversationHistory = [
          ...conversationHistory,
          {
            role: 'user',
            content: userMessage,
            timestamp,
            language: userLanguage,
          },
          {
            role: 'assistant',
            content: aiResponse,
            timestamp,
            language: aiLanguage,
          },
        ]
        setConversationHistory(newConversationHistory)

        const chat: ChatRecord = {
          chat_id: generateId('chat'),
          session_id: sessionRecord.session_id,
          person_id: personProfile?.person_id,
          user_message: userMessage,
          user_message_length: userMessage.length,
          typing_duration: typingDuration,
          edit_count: editCount,
          retry_count: 0,
          ai_response: aiResponse,
          ai_response_length: aiResponse.length,
          response_latency: responseLatency,
          intent,
          sentiment,
          is_about_trung: userMessage.toLowerCase().includes('trung'),
          is_general_question: !userMessage.toLowerCase().includes('trung'),
          risk_flags: riskFlags,
          abuse_detected: riskFlags.length > 0,
          timestamp,
          message_index: chats.length,
          conversation_context: newConversationHistory
            .slice(-6)
            .map((m) => m.content),
          metadata: {
            user_language: userLanguage,
            ai_language: aiLanguage,
            conversation_length: newConversationHistory.length / 2,
            full_conversation: newConversationHistory,
            browser_language: navigator.language,
            page_url: window.location.href,
          },
        }

        setChats((prev) => [...prev, chat])
        saveChat(chat)

        if (sessionRecord) {
          const updatedSession = {
            ...sessionRecord,
            chat_messages: sessionRecord.chat_messages + 1,
          }
          setSessionRecord(updatedSession)
          saveSession(updatedSession)
        }
      } catch (error) {
        // Silent failure
      }
    },
    [hasConsent, sessionRecord, personProfile, chats, conversationHistory],
  )

  const saveSession = (session: SessionRecord) => {
    try {
      const sessions = safeLocalStorageGet('sessions') || []
      const index = sessions.findIndex(
        (s: SessionRecord) => s.session_id === session.session_id,
      )
      if (index >= 0) {
        sessions[index] = session
      } else {
        sessions.push(session)
      }
      safeLocalStorageSet('sessions', sessions)
    } catch (error) {
      // Silent failure
    }
  }

  const saveEvents = (events: EventRecord[]) => {
    try {
      const stored = safeLocalStorageGet('events') || []
      safeLocalStorageSet('events', [...stored, ...events])
    } catch (error) {
      // Silent failure
    }
  }

  const saveChat = (chat: ChatRecord) => {
    try {
      const chats = safeLocalStorageGet('chats') || []
      chats.push(chat)
      safeLocalStorageSet('chats', chats)
    } catch (error) {
      // Silent failure
    }
  }

  useEffect(() => {
    if (!hasConsent) return

    const handleClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement
      trackEvent({
        event_type: 'click',
        event_category: 'click',
        viewport_x: e.clientX,
        viewport_y: e.clientY,
        element_type: target.tagName.toLowerCase(),
        element_id: target.id,
        element_class: target.className,
        element_text: target.textContent?.substring(0, 100),
        metadata: { button: e.button },
      })

      if (sessionRecord) {
        sessionRecord.clicks += 1
      }
    }

    let scrollTimeout: NodeJS.Timeout
    const handleScroll = () => {
      clearTimeout(scrollTimeout)
      scrollTimeout = setTimeout(() => {
        const scrollDepth = Math.round(
          (window.scrollY /
            (document.documentElement.scrollHeight - window.innerHeight)) *
            100,
        )

        trackEvent({
          event_type: 'scroll',
          event_category: 'scroll',
          scroll_position: window.scrollY,
          scroll_depth: scrollDepth,
          metadata: {},
        })

        if (sessionRecord && scrollDepth > sessionRecord.scroll_depth_max) {
          sessionRecord.scroll_depth_max = scrollDepth
        }
      }, 150)
    }

    const resetIdleTimer = () => {
      lastActivityTime.current = Date.now()

      if (isIdle.current) {
        trackEvent({
          event_type: 'idle_end',
          event_category: 'idle',
          metadata: {},
        })
        isIdle.current = false
      }

      clearTimeout(idleTimeout.current)
      idleTimeout.current = setTimeout(() => {
        trackEvent({
          event_type: 'idle_start',
          event_category: 'idle',
          metadata: {},
        })
        isIdle.current = true
      }, 30000)
    }

    const handleVisibilityChange = () => {
      trackEvent({
        event_type: document.hidden ? 'page_blur' : 'page_focus',
        event_category: 'page',
        metadata: {},
      })
    }

    const handleMouseMove = () => {
      lastActivityTime.current = Date.now()
    }

    const handleKeyPress = () => {
      lastActivityTime.current = Date.now()
    }

    document.addEventListener('click', handleClick)
    document.addEventListener('scroll', handleScroll)
    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('keypress', handleKeyPress)
    document.addEventListener('visibilitychange', handleVisibilityChange)

    resetIdleTimer()

    return () => {
      document.removeEventListener('click', handleClick)
      document.removeEventListener('scroll', handleScroll)
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('keypress', handleKeyPress)
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      clearTimeout(idleTimeout.current)
      if (activityInterval.current) {
        clearInterval(activityInterval.current)
      }
      if (saveInterval.current) {
        clearInterval(saveInterval.current)
      }
    }
  }, [hasConsent, trackEvent, sessionRecord])

  useEffect(() => {
    return () => {
      if (eventQueue.current.length > 0) {
        saveEvents(eventQueue.current)
      }

      if (sessionRecord) {
        const finalSession = {
          ...sessionRecord,
          end_time: new Date().toISOString(),
          duration: Math.floor((Date.now() - sessionStartTime.current) / 1000),
        }
        saveSession(finalSession)
      }
    }
  }, [sessionRecord])

  return {
    hasConsent,
    consentRecord,
    personProfile,
    sessionRecord,
    events,
    chats,
    conversationHistory,
    handleConsent,
    trackEvent,
    trackChatMessage,
  }
}
